import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { useServices } from "@/app/use-services";
import { useMomentOnce } from "@/app/use-moment";
import { errorMessage } from "@/lib/api/errors";
import { Button, LinkButton } from "@/ui/Button";
import { ErrorView } from "@/ui/ErrorView";
import { MicIcon } from "@/ui/MicIcon";
import { Row } from "@/ui/Row";
import { Sheet } from "@/ui/Sheet";
import { Screen } from "@/ui/Screen";
import { Body, Display, Label } from "@/ui/Text";
import { Working } from "@/ui/Working";
import { deviceTimeZone, localAmount } from "./price";
import { SubscribeOffer } from "./SubscribeOffer";
import { useInvalidatePlan, usePlan } from "./use-plan";

/** How often to ask again while Stripe's webhook is on its way. */
const CONFIRM_POLL_MS = 2000;

/**
 * The paywall, as a page of its own. A locked account lands here from
 * anywhere in the app; Stripe sends people back here with `?checkout=…`.
 */
export function SubscribePage() {
  const [params] = useSearchParams();
  const checkout = params.get("checkout");
  const plan = usePlan();
  const invalidatePlan = useInvalidatePlan();
  const waitingForPayment = checkout === "success" && plan.data !== undefined && !plan.data.active;
  const offerShown = plan.data !== undefined && !plan.data.active && !waitingForPayment;
  useMomentOnce(offerShown ? { name: "paywall shown", surface: plan.data?.locked ? "locked" : "upgrade" } : null);
  useMomentOnce(checkout === "cancelled" ? { name: "checkout cancelled" } : null);

  useEffect(() => {
    // Back from Stripe: the webhook may land a moment after the browser does.
    if (!waitingForPayment) return;
    const timer = window.setInterval(() => void invalidatePlan(), CONFIRM_POLL_MS);
    return () => window.clearInterval(timer);
  }, [waitingForPayment, invalidatePlan]);

  if (plan.isPending) return <Working message="One moment…" />;
  if (plan.isError) {
    return (
      <Screen middle>
        <ErrorView message={errorMessage(plan.error)} onRetry={() => void plan.refetch()} />
      </Screen>
    );
  }
  if (plan.data.active) {
    return checkout === "success" ? <Welcome paidUntil={plan.data.expires_at} /> : <Navigate to="/today" replace />;
  }
  if (waitingForPayment) return <Working message="Confirming your payment…" />;

  return (
    <Screen middle>
      <Label>Antar</Label>
      <Display className="mt-3">{plan.data.locked ? "Your free minutes are up." : "Keep talking with Antar."}</Display>
      <Body className="mt-3 text-mid">
        {plan.data.locked
          ? "Subscribe to keep talking with her and to keep your day, week and month."
          : "One plan, everything included."}
      </Body>
      {checkout === "cancelled" && (
        <p className="mt-4 border-l-2 border-ink pl-3 text-[15px]">Nothing was charged.</p>
      )}
      <div className="mt-6">
        <SubscribeOffer />
      </div>
      <FooterLinks />
    </Screen>
  );
}

/** Remembers which paid period was reported, so a revisit is not a second purchase. */
const PURCHASE_REPORTED_KEY = "antar.purchase-reported";

function useReportPurchase(paidUntil: Date | undefined) {
  const { analytics } = useServices();
  useEffect(() => {
    const period = paidUntil?.toISOString() ?? "unknown";
    try {
      if (localStorage.getItem(PURCHASE_REPORTED_KEY) === period) return;
      localStorage.setItem(PURCHASE_REPORTED_KEY, period);
    } catch {
      // Storage blocked: report anyway; a rare duplicate beats a missing sale.
    }
    analytics.track({ name: "purchase completed", ...localAmount(deviceTimeZone()), transactionId: `antar-${period}` });
  }, [paidUntil, analytics]);
}

function Welcome({ paidUntil }: { readonly paidUntil: Date | undefined }) {
  const navigate = useNavigate();
  const { analytics } = useServices();
  useReportPurchase(paidUntil);
  return (
    <Screen middle centred>
      <Display>You’re in.</Display>
      <Body className="mt-3 text-mid">Thank you. She’s ready when you are.</Body>
      <div className="mt-8 flex w-full flex-col items-center gap-3">
        <Button
          label="Tap to speak to Antar"
          icon={<MicIcon />}
          onClick={() => {
            analytics.track({ name: "speak tapped", from: "welcome" });
            navigate("/talk/voice");
          }}
        />
        <LinkButton label="Go to today" onClick={() => navigate("/today")} />
      </div>
    </Screen>
  );
}

/**
 * "Not now" goes back to Today, which a locked account sees as its headline
 * with the reading held back. "Account" keeps sign-out, privacy and deletion
 * one tap away without putting them in front of the offer.
 */
function FooterLinks() {
  const navigate = useNavigate();
  const { session, analytics } = useServices();
  const [account, setAccount] = useState(false);
  return (
    <>
      {/* -mx-2 lines the links' text up with the page edge; the buttons pad themselves. */}
      <div className="-mx-2 mt-6 flex items-center justify-between">
        <LinkButton
          label="Not now"
          onClick={() => {
            analytics.track({ name: "paywall dismissed" });
            navigate("/today");
          }}
        />
        <LinkButton label="Account" onClick={() => setAccount(true)} />
      </div>
      <Sheet open={account} onClose={() => setAccount(false)} label="Account">
        <Label>Account</Label>
        <div className="mt-4 flex flex-col">
          <Row
            value={<span className="label text-ink">Sign out</span>}
            onClick={() => {
              analytics.track({ name: "signed out" });
              void session.signOut().then(() => navigate("/", { replace: true }));
            }}
          />
          <Row value="Privacy" onClick={() => navigate("/you/privacy")} />
          <Row value="Delete my account" onClick={() => navigate("/you/delete")} />
        </div>
      </Sheet>
    </>
  );
}
